import {
  commissionDiscounts,
  betTypeKeys,
  TWO_WAY_SOCCER_LEAGUE_KEYWORDS,
} from '../consts/constants';
import {
  lookupTeamDisplay,
  applyPeriodPrefix,
  getPeriodPrefix,
} from '@/consts/teamsDisplayNames';

// Single source of truth for mapping a user's sort-order booleans to the
// canonical analytics label. Used by both the settings event (setting_changed)
// and the person-property snapshot, so they can never drift apart when the
// sort options change. Priority order matters: first truthy flag wins, and it
// must match how the UI resolves the flags (Account initSettings, league
// header) — rotation, then matched volume, then open interest — so analytics
// reports the sort the user actually sees.
export function canonicalSortFromUser(user) {
  if (!user) return 'default';
  if (user.defaultRotationNumbers) return 'rotation_number';
  if (user.matchedVolume) return 'matched_volume';
  if (user.openInterest) return 'open_interest';
  return 'default';
}

export function dollarFormat(value) {
  let matchedVolume = value ? value : 0;
  if (matchedVolume < 100000) matchedVolume = matchedVolume.toFixed(2);
  else matchedVolume = matchedVolume.toFixed(0);
  if (matchedVolume >= 1000)
    matchedVolume = matchedVolume
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return matchedVolume;
}

// Compact money label for tight surfaces (home section cards): whole dollars
// below $1K, one-decimal K/M above — $847, $86.8K, $1.2M. dollarFormat stays
// as-is for surfaces that want the exact figure.
export function compactDollarFormat(value) {
  const amount = Number(value) || 0;
  const scaled = (n) => {
    const s = n.toFixed(1);
    return s.endsWith('.0') ? s.slice(0, -2) : s;
  };
  if (amount >= 999950) return `${scaled(amount / 1000000)}M`;
  if (amount >= 1000) return `${scaled(amount / 1000)}K`;
  return Math.round(amount).toString();
}

export function roundToFixed2(value) {
  const n = Number.parseFloat(value);
  const rounded = n.toFixed(2);
  // A value whose magnitude rounds away at 2 decimals is zero as far as the
  // display is concerned. `(-0.000321).toFixed(2)` is "-0.00", which reads as a
  // real (if tiny) negative amount — drop the sign once there's nothing left of it.
  return Number(rounded) === 0 ? rounded.replace('-', '') : rounded;
}

// Sign for balances we render as `sign + $ + magnitude`. Keyed off the ROUNDED
// value, not the raw one, so a balance like -0.000321 — which prints as 0.00 —
// isn't signed into "-$0.00".
export function balanceDisplaySign(value, decimals = 2) {
  const n = Number.parseFloat(value);
  if (!Number.isFinite(n) || Number(n.toFixed(decimals)) === 0) return '';
  return n < 0 ? '-' : '';
}

// Floor a decimal-odds value at 3 places. We floor (instead of round) so the
// displayed odds never *overstate* the actual American line — risk × (D - 1)
// can be slightly under, never over, the real win. The 1e-9 epsilon absorbs
// IEEE 754 representation error (e.g. +101 stored as 2.00999…) so values that
// land exactly on a 3-decimal tick render as that tick rather than getting
// nudged a step down.
function floor3(value) {
  return Math.floor(value * 1000 + 1e-9) / 1000;
}

// Floor at 3 decimals but always keep at least 2. Reads as a clean ladder:
// ... 1.99 → 1.995 → 2.00 → 2.005 → 2.01 ... (no bare "2" between half-step
// neighbours, no over-precise "2.000" trailing zero either).
export function formatDecimalOdds(value) {
  const n = Number.parseFloat(value);
  if (Number.isNaN(n)) return '';
  return floor3(n).toFixed(3).replace(/0$/, '');
}

// Same shape as `formatDecimalOdds`. Kept as a separate export because the
// editable odds selector imports it explicitly; if the row vs. input format
// ever needs to diverge again, this is where it would happen.
export const formatDecimalOddsForInput = formatDecimalOdds;

export function roundTo3(value) {
  const stringValue = value.toString();
  if (stringValue.includes('.') && stringValue.split('.')[1].length > 3) {
    return parseFloat(value.toFixed(3));
  }
  return value;
}

export function capitalize(value) {
  if (!value) return value;
  const s = value.toString();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Trailing flags the API tacks onto prop names (e.g. "MUST START") that we
 * surface elsewhere in the UI rather than crammed into the title. Keep this
 * narrow on purpose — anything that's actually part of the bet description
 * (totals, OU, etc.) should not be stripped.
 */
// Real-world MLB prop names from api.4casters.io look like:
//   "BRANDON YOUNG (TOTAL STRIKEOUTS)(MUST START)"
//   "JOC PEDERSON (HOME RUNS)(MUST START)"
// The flag is wrapped in parens and may not be the absolute end of the
// string, so the patterns are non-anchored and global.
const PROP_FLAG_PATTERNS = [
  /\s*\(\s*MUST\s*START\s*\)\s*/gi,
  /\s+MUST\s*START\b\s*/gi,
];

export function stripPropFlags(name) {
  if (name === undefined || name === null) return name;
  let s = name.toString();
  for (const re of PROP_FLAG_PATTERNS) {
    s = s.replace(re, ' ');
  }
  return s.replace(/\s+/g, ' ').trim();
}

export function hasMustStartFlag(name) {
  if (!name) return false;
  return /MUST\s*START/i.test(name.toString());
}

// World Cup leagues arrive with a few interchangeable spellings of the league
// code (dashed / undashed, plus the "-LIVE" variant the live feed sometimes
// tacks on). Normalise to UPPERCASE-no-dashes so a single membership check
// covers them all.
const WORLD_CUP_LEAGUE_CODES = new Set(['WORLDCUP', 'WORLDCUPLIVE']);

function normalizeLeagueCode(league) {
  if (!league) return '';
  return league
    .toString()
    .toUpperCase()
    .replace(/[-_\s]/g, '');
}

export function isWorldCupLeague(league) {
  return WORLD_CUP_LEAGUE_CODES.has(normalizeLeagueCode(league));
}

// "To Advance" World Cup specials live in the regular WORLD-CUP league but the
// feed tags each participant's mainPitcher with the literal "To Advance". These
// are 2-way outright markets (a team advances or not), NOT a 90-minute match, so
// they must be treated differently from a normal World Cup game: no 90+INJ tag
// and rendered as a plain 2-way moneyline (no draw).
export function isToAdvanceGame(game) {
  if (!game) return false;
  return (game.participants || []).some(
    (p) => p?.mainPitcher?.toString().trim().toLowerCase() === 'to advance',
  );
}

// World Cup games are bet for 90+INJ time only (regulation + stoppage), both
// pre-match and live. We surface a sticker on these games so bettors know extra
// time doesn't count. "To Advance" specials are excluded — they aren't a
// 90-minute match, so the 90+INJ tag must not show on them.
export function isWorldCupGame(game) {
  if (!game) return false;
  return isWorldCupLeague(game.league) && !isToAdvanceGame(game);
}

// Hover copy for the 90+INJ sticker. Single source of truth.
export const NINETY_PLUS_INJ_TOOLTIP =
  'This bet is only for 90+INJ time. Anything that happens in extra time will not count towards this bet.';

// Hover copy for the "To Advance" sticker. Unlike a 90-minute match, an outright
// "to advance" special settles on which team progresses to the next round, so
// extra time and penalties DO count. Single source of truth.
export const TO_ADVANCE_TOOLTIP =
  'Outright market: which team advances to the next round. Settled on the final result of the tie, including extra time and penalties.';

/** Roman-numeral suffixes on prop titles (e.g. Robert Williams III). */
const ALL_CAPS_ROMAN_NUMERAL = /^(?:I{1,3}|IV|VI{0,3}|IX|XI{0,3}|XIV|XIX|XX)$/;

function titleCaseAllCapsWord(word) {
  return word.charAt(0) + word.slice(1).toLowerCase();
}

/**
 * Whether an ALL-CAPS word should stay uppercase when title-casing an event name.
 * Only true for standalone 2-letter initials (CJ, OG, PJ) and Roman numerals (III).
 */
function shouldPreserveAllCapsWord(word, nextChar) {
  if (ALL_CAPS_ROMAN_NUMERAL.test(word)) return true;
  // De'Aaron, De'Andre — prefix before apostrophe is not an initial.
  if (word.length === 2 && nextChar !== "'") return true;
  return false;
}

/**
 * Event titles from the API are either legacy ALL CAPS (main markets) or
 * mixed-case (player props). This helper is the single display normalizer for
 * prop titles (All Props, GameProps, bet modals, etc.).
 *
 * Rules (each came from a prior bug — keep them when changing this):
 * - Mixed-case input is returned as-is (McCollum, O'Neale from the API).
 * - ALL CAPS input is title-cased word-by-word via `\b` boundaries, so hyphens
 *   split correctly (CROW-ARMSTRONG → Crow-Armstrong) and parens work
 *   ((TOTAL STRIKEOUTS) → (Total Strikeouts)).
 * - Standalone 2-letter tokens stay ALL CAPS (CJ McCollum, OG Anunoby).
 * - 2-letter tokens before `'` are title-cased (DE'AARON → De'Aaron).
 * - 3-letter surnames are title-cased (FOX → Fox); Roman numerals stay (III).
 */
export function formatEventNameForDisplay(name) {
  if (name === undefined || name === null) return name;
  const stripped = stripPropFlags(name);
  const s = stripped.toString().trim();
  if (!s) return s;
  if (/[a-z]/.test(s)) return s;
  return s.replace(/\b[\p{L}]+\b/gu, (word, offset) => {
    const nextChar = s[offset + word.length] ?? '';
    if (shouldPreserveAllCapsWord(word, nextChar)) return word;
    return titleCaseAllCapsWord(word);
  });
}

export function getSideStringChatMessages(game, session) {
  let sideString;
  if (session.type === 'moneyline') {
    if (game.league.toLowerCase().includes('future')) {
      const team = game.participants.find((p) => p._id === session.participant);
      sideString = `${team.longName} ${team.futuresSide}`;
    } else {
      sideString = game.participants.find(
        (p) => p._id === session.participant,
      ).longName;
    }
  } else if (session.type === 'spread') {
    const participantSpread = game.participants.find(
      (p) => p._id === session.participant,
    );
    if (participantSpread) {
      sideString = `${participantSpread.longName}  ${signOdds(session.spread)}`;
    }
  } else if (session.type === 'total') {
    if (game.league.toLowerCase().includes('1h')) {
      sideString = `1H ${capitalize(session.OU)} ${session.total}`;
    } else {
      sideString = `${capitalize(session.OU)} ${session.total}`;
    }
  }
  return sideString;
}

export function signOdds(odds) {
  const n = Number(odds);
  if (!n) return n;
  // if there are american numbers with decimals, it will set n to display only one decimal with its sign only if it doesnt contain .0 otherwise will display the integer part or just display the received value//
  if (n.toString().includes('.') && n.toString().length > 5) {
    if (n.toFixed(1).includes('.0')) {
      return n > 0 ? `+${n.toFixed(0)}` : `${n.toFixed(0)}`;
    } else if (!n.toFixed(1).includes('.0')) {
      return n > 0 ? `+${n.toFixed(1)}` : `${n.toFixed(1)}`;
    }
  }
  return n > 0 ? `+${n}` : `${n}`;
}

export function convertAmericanToImpliedProbability(americanOdds) {
  return convertAmericanToPercent(americanOdds) * 100;
}

export function convertImpliedProbabilityToAmerican(probability) {
  return convertPercentToAmerican(probability / 100);
}

// Worst American odds a taker accepts given a slippage tolerance in
// implied-probability percentage points. Mirrors go-api SlippageFloorOdds so
// the settings preview and the value the order carries never diverge. A
// non-positive tolerance returns the requested odds unchanged.
export function slippageFloorOdds(requestedOdds, slippagePct) {
  const pts = Number(slippagePct);
  const odds = Number(requestedOdds);
  if (!Number.isFinite(pts) || pts <= 0 || !Number.isFinite(odds)) return odds;
  // implied probability on a 0–100 scale
  const p = convertAmericanToImpliedProbability(odds);
  let target = p + pts;
  if (target > 99) target = 99;
  // Never return a floor tighter than the requested price. When the cap
  // collapses the target back to (or past) the request, return the input
  // verbatim instead of round-tripping — prob→odds conversion carries float
  // error that would land a hair tighter, and the engine's match compares
  // odds exactly (mirrors go-api SlippageFloorOdds).
  if (target <= p) return odds;
  // Cap boundary snaps to exactly −9900: the prob→odds round-trip at 99%
  // lands ~1e-12 off, and the engine's $gte compares odds exactly — a hair
  // off would exclude a maker at exactly −9900 (mirrors go-api's snap;
  // spec-review S3 flagged the frontend missing it).
  if (target === 99) return -9900;
  return convertImpliedProbabilityToAmerican(target);
}

// Returns the exact decimal-odds equivalent of an American line.
// Previously truncated to 3 places via `Math.floor`, which silently moved the
// implied price by up to 0.001 — meaningful money on large bets (e.g. ~$150
// on a $30k bet at -146, where exact decimal is 1.6849 but floor gave 1.684).
// Lossless now; downstream display formatters round when needed.
export function convertAmericanToDecimal(odds) {
  // Defensive fallback (preserved from the original): return even-money for
  // 0, NaN, ±Infinity, undefined, null, or non-numeric input. Without this
  // `100 / -0 = -Infinity` and NaN would propagate and render as the literal
  // string "-Infinity" / "NaN" if it reached a display path.
  const n = Number(odds);
  if (!Number.isFinite(n) || n === 0 || Math.abs(n) === 100) return 2;
  if (n > 0) return n / 100 + 1;
  return 100 / -n + 1;
}

export function addComa(number) {
  const sumUntaken = Object.values(number)[0]
    ? Object.values(number)[0]
    : number;
  const roundedNumber = Math.round(sumUntaken);
  const str = roundedNumber.toString().split('.');
  str[0] = str[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return str.join('.');
}

export function noDecimal(value) {
  const n = Number.parseFloat(value);
  return Number(n.toFixed(0));
}

export function calculateWin(bet, odds) {
  if (odds === 0) return 0;
  const resultWin = odds > 0 ? (bet / 100) * odds : (bet / -odds) * 100;
  if (Number.isNaN(Number.parseFloat(resultWin))) return 0;
  return resultWin;
}

export function calculateRisk(win, odds) {
  if (odds === 0) return 0;
  const resultRisk = odds > 0 ? (win * 100) / odds : (win * -odds) / 100;
  if (Number.isNaN(Number.parseFloat(resultRisk))) return 0;
  return resultRisk;
}

// Which offer field the user's create-offer unit mode holds fixed at the
// given american odds ('risk' | 'win'). The Cardano anchors the win amount
// on minus odds and the risk amount on plus odds.
export function getAnchoredOfferSide(mode, odds) {
  if (mode === 'winAUnit') return 'win';
  if (mode === 'theCardano') return odds <= -100 ? 'win' : 'risk';
  return 'risk';
}

export function getMainSpreads(game) {
  if (game.league.toLowerCase().includes('future')) {
    return [[], []];
  }

  if (
    (game.mainHomeSpread && game.mainAwaySpread) ||
    (Math.abs(game.mainHomeSpread) === 0 && Math.abs(game.mainAwaySpread) === 0)
  ) {
    return [game.mainHomeSpread, game.mainAwaySpread];
  }

  const homeNumbers = Object.keys(game.homeSpreads)
    .map((x) => Number(x))
    .sort((x, y) => x - y);
  const awayNumbers = Object.keys(game.awaySpreads)
    .map((x) => Number(x))
    .sort((x, y) => x - y);

  if (homeNumbers[1] && awayNumbers[1]) {
    return [homeNumbers[1], awayNumbers[1]];
  }
  if (homeNumbers[1] === 0 && awayNumbers[1] === 0) {
    return [homeNumbers[1], awayNumbers[1]];
  }
  return [[], []];
}

export function getMainTotal(game) {
  if (game.league.toLowerCase().includes('future')) {
    return [[], []];
  }
  return game.mainTotal ? game.mainTotal : '';
}

export function hasSelfSession(sessions, userId, getSelfSession = false) {
  if (sessions && sessions.length && userId) {
    const selfSession = sessions.find(
      (session) =>
        session.source !== 'fillerData' &&
        session.createdBy &&
        session.createdBy.toString() === userId.toString(),
    );

    if (selfSession) {
      return getSelfSession ? selfSession : true;
    }
  }
  return false;
}

export function selfSessionNotBest(sessionsList, userId) {
  const selfSessionIndex = sessionsList.findIndex((sessions) =>
    sessions.some(
      (session) => session.createdBy && session.createdBy === userId,
    ),
  );
  return selfSessionIndex > 0;
}

export function simpleHasSelfSession(session, userId) {
  if (!session) return false;
  if (session.createdBy && session.createdBy === userId) {
    return true;
  }
  return false;
}

export function hasMarketMakerSession(sessions) {
  if (sessions && sessions.length) {
    // not session source means that it comes from the api, so there are orders
    const selfSession = sessions.find((session) => !session.source);

    if (selfSession) {
      return true;
    }
  }
  return false;
}

export function addFillerData(
  sessions = [],
  type,
  side,
  number,
  gameID,
  yesNo,
) {
  const fillerDataNeeded = 4 - sessions?.length ?? 0;
  if (fillerDataNeeded < 1) {
    return sessions;
  }
  const fillerData = [
    {
      gameID,
      source: 'fillerData',
      createdBy: 'x',
      type,
      participantID: type === 'total' ? null : side,
      OU: type === 'total' ? side : null,
      spread: type === 'spread' ? Number(number) : null,
      total: type === 'total' ? Number(number) : null,
      side: yesNo,
      market: type === 'moneyline1x2' && side ? side : 'draw',
    },
  ];
  return [...sessions, ...Array(fillerDataNeeded).fill(fillerData)];
}

export function combineGameOdds(game) {
  const gameObj = JSON.parse(JSON.stringify(game));
  if (gameObj.awaySpreads) {
    Object.keys(gameObj.awaySpreads).forEach((spread) => {
      if (gameObj.awaySpreads[spread]) {
        if (gameObj.awaySpreads[spread].length) {
          const currentUpdatedSpreads = {};
          gameObj.awaySpreads[spread].forEach((spreadObj, index) => {
            if (spreadObj.odds) {
              if (!currentUpdatedSpreads[`_${spreadObj.odds}`]) {
                currentUpdatedSpreads[`_${spreadObj.odds}`] = [];
              }
              currentUpdatedSpreads[`_${spreadObj.odds}`].push(spreadObj);
            } else {
              // fillerData
              currentUpdatedSpreads[`fillerData_${index}`] = [spreadObj];
            }
          });
          if (Object.keys(currentUpdatedSpreads).length) {
            gameObj.awaySpreads[spread] = Object.values(currentUpdatedSpreads);
          }
        }

        // Add filler data for awaySpreads
        gameObj.awaySpreads[spread] = addFillerData(
          gameObj.awaySpreads[spread],
          'spread',
          game?.participants?.[0]?.id,
          spread,
          game.id,
        );
      }
    });
    if (gameObj.mainAwaySpread) {
      gameObj.awaySpreads[gameObj.mainAwaySpread] = addFillerData(
        gameObj.awaySpreads[gameObj.mainAwaySpread],
        'spread',
        game?.participants?.[0]?.id,
        gameObj.mainAwaySpread,
        game.id,
      );
    }
  }

  if (gameObj.homeSpreads) {
    Object.keys(gameObj.homeSpreads).forEach((spread) => {
      if (gameObj.homeSpreads[spread]) {
        if (gameObj.homeSpreads[spread].length) {
          const currentUpdatedSpreads = {};
          gameObj.homeSpreads[spread].forEach((spreadObj, index) => {
            if (spreadObj.odds) {
              // Adding `_` to key for maintaining the order
              if (!currentUpdatedSpreads[`_${spreadObj.odds}`]) {
                currentUpdatedSpreads[`_${spreadObj.odds}`] = [];
              }
              currentUpdatedSpreads[`_${spreadObj.odds}`].push(spreadObj);
            } else {
              // fillerData
              currentUpdatedSpreads[`fillerData_${index}`] = [spreadObj];
            }
          });
          if (Object.keys(currentUpdatedSpreads).length) {
            gameObj.homeSpreads[spread] = Object.values(currentUpdatedSpreads);
          }
        }

        // Add filler data for homeSpreads
        gameObj.homeSpreads[spread] = addFillerData(
          gameObj.homeSpreads[spread],
          'spread',
          game?.participants?.[1]?.id,
          spread,
          game.id,
        );
      }
    });
    if (gameObj.mainHomeSpread) {
      gameObj.homeSpreads[gameObj.mainHomeSpread] = addFillerData(
        gameObj.homeSpreads[gameObj.mainHomeSpread],
        'spread',
        game?.participants?.[1]?.id,
        gameObj.mainHomeSpread,
        game.id,
      );
    }
  }

  if (gameObj.over) {
    Object.keys(gameObj.over).forEach((total) => {
      if (gameObj.over[total]) {
        if (gameObj.over[total].length) {
          const currentUpdatedSpreads = {};
          gameObj.over[total].forEach((totalObj, index) => {
            if (totalObj.odds) {
              // Adding `_` to key for maintaining the order
              if (!currentUpdatedSpreads[`_${totalObj.odds}`]) {
                currentUpdatedSpreads[`_${totalObj.odds}`] = [];
              }
              currentUpdatedSpreads[`_${totalObj.odds}`].push(totalObj);
            } else {
              // fillerData
              currentUpdatedSpreads[`fillerData_${index}`] = [totalObj];
            }
          });
          if (Object.keys(currentUpdatedSpreads).length) {
            gameObj.over[total] = Object.values(currentUpdatedSpreads);
          }
        }

        // Add filler data for over
        gameObj.over[total] = addFillerData(
          gameObj.over[total],
          'total',
          'over',
          total,
          game.id,
        );
      }
    });
    if (gameObj.mainTotal) {
      gameObj.over[gameObj.mainTotal] = addFillerData(
        gameObj.over[gameObj.mainTotal],
        'total',
        'over',
        gameObj.mainTotal,
        game.id,
      );
    }
  }

  if (gameObj.under) {
    Object.keys(gameObj.under).forEach((total) => {
      if (gameObj.under[total]) {
        if (gameObj.under[total].length) {
          const currentUpdatedSpreads = {};
          gameObj.under[total].forEach((totalObj, index) => {
            if (totalObj.odds) {
              // Adding `_` to key for maintaining the order
              if (!currentUpdatedSpreads[`_${totalObj.odds}`]) {
                currentUpdatedSpreads[`_${totalObj.odds}`] = [];
              }
              currentUpdatedSpreads[`_${totalObj.odds}`].push(totalObj);
            } else {
              // fillerData
              currentUpdatedSpreads[`fillerData_${index}`] = [totalObj];
            }
          });
          if (Object.keys(currentUpdatedSpreads).length) {
            gameObj.under[total] = Object.values(currentUpdatedSpreads);
          }
        }

        // Add filler data for under
        gameObj.under[total] = addFillerData(
          gameObj.under[total],
          'total',
          'under',
          total,
          game.id,
        );
      }
    });
    if (gameObj.mainTotal) {
      gameObj.under[gameObj.mainTotal] = addFillerData(
        gameObj.under[gameObj.mainTotal],
        'total',
        'under',
        gameObj.mainTotal,
        game.id,
      );
    }
  }

  if (gameObj.awayMoneylines) {
    if (gameObj.awayMoneylines.length) {
      const currentUpdatedSpreads = {};
      gameObj.awayMoneylines.forEach((moneyLineObj, index) => {
        if (moneyLineObj.odds) {
          if (!currentUpdatedSpreads[`_${moneyLineObj.odds}`]) {
            currentUpdatedSpreads[`_${moneyLineObj.odds}`] = [];
          }
          currentUpdatedSpreads[`_${moneyLineObj.odds}`].push(moneyLineObj);
        } else {
          // fillerData
          currentUpdatedSpreads[`fillerData_${index}`] = [moneyLineObj];
        }
      });
      if (Object.keys(currentUpdatedSpreads).length) {
        gameObj.awayMoneylines = Object.values(currentUpdatedSpreads);
      }
    }

    // Add filler data for awayMoneylines
    gameObj.awayMoneylines = addFillerData(
      gameObj.awayMoneylines,
      'moneyline',
      game?.participants?.[0]?.id,
      null,
      game.id,
    );
  }

  if (gameObj.homeMoneylines) {
    if (gameObj.homeMoneylines.length) {
      const currentUpdatedSpreads = {};
      gameObj.homeMoneylines.forEach((moneyLineObj, index) => {
        if (moneyLineObj.odds) {
          if (!currentUpdatedSpreads[`_${moneyLineObj.odds}`]) {
            currentUpdatedSpreads[`_${moneyLineObj.odds}`] = [];
          }
          currentUpdatedSpreads[`_${moneyLineObj.odds}`].push(moneyLineObj);
        } else {
          // fillerData
          currentUpdatedSpreads[`fillerData_${index}`] = [moneyLineObj];
        }
      });
      if (Object.keys(currentUpdatedSpreads).length) {
        gameObj.homeMoneylines = Object.values(currentUpdatedSpreads);
      }
    }

    // Add filler data for homeMoneylines
    gameObj.homeMoneylines = addFillerData(
      gameObj.homeMoneylines,
      'moneyline',
      game?.participants?.[1]?.id,
      null,
      game.id,
    );
  }

  ///////////////////
  //awayMoneylines1x2.awayYes
  if (gameObj.awayMoneylines1x2 && gameObj.awayMoneylines1x2.awayYes) {
    if (gameObj.awayMoneylines1x2.awayYes.length) {
      const currentUpdatedSpreads = {};
      gameObj.awayMoneylines1x2.awayYes.forEach((moneyLineObj, index) => {
        if (moneyLineObj.odds) {
          if (!currentUpdatedSpreads[`_${moneyLineObj.odds}`]) {
            currentUpdatedSpreads[`_${moneyLineObj.odds}`] = [];
          }
          currentUpdatedSpreads[`_${moneyLineObj.odds}`].push(moneyLineObj);
        } else {
          // fillerData
          currentUpdatedSpreads[`fillerData_${index}`] = [moneyLineObj];
        }
      });
      if (Object.keys(currentUpdatedSpreads).length) {
        gameObj.awayMoneylines1x2.awayYes = Object.values(
          currentUpdatedSpreads,
        );
      }
    }

    // Add filler data for awayMoneylines1x2.awayYes
    gameObj.awayMoneylines1x2.awayYes = addFillerData(
      gameObj.awayMoneylines1x2.awayYes,
      'moneyline1x2',
      game?.participants?.[0]?.id,
      null,
      game.id,
      'yes',
    );
  }
  ///awayMoneylines1x2.awayNo
  if (gameObj.awayMoneylines1x2 && gameObj.awayMoneylines1x2.awayNo) {
    if (gameObj.awayMoneylines1x2.awayNo.length) {
      const currentUpdatedSpreads = {};
      gameObj.awayMoneylines1x2.awayNo.forEach((moneyLineObj, index) => {
        if (moneyLineObj.odds) {
          if (!currentUpdatedSpreads[`_${moneyLineObj.odds}`]) {
            currentUpdatedSpreads[`_${moneyLineObj.odds}`] = [];
          }
          currentUpdatedSpreads[`_${moneyLineObj.odds}`].push(moneyLineObj);
        } else {
          // fillerData
          currentUpdatedSpreads[`fillerData_${index}`] = [moneyLineObj];
        }
      });
      if (Object.keys(currentUpdatedSpreads).length) {
        gameObj.awayMoneylines1x2.awayNo = Object.values(currentUpdatedSpreads);
      }
    }

    // Add filler data for awayMoneylines1x2.awayNo
    gameObj.awayMoneylines1x2.awayNo = addFillerData(
      gameObj.awayMoneylines1x2.awayNo,
      'moneyline1x2',
      game?.participants?.[0]?.id,
      null,
      game.id,
      'no',
    );
  }

  ////  //homeMoneylines1x2.homeYes
  if (gameObj.homeMoneylines1x2 && gameObj.homeMoneylines1x2.homeYes) {
    if (gameObj.homeMoneylines1x2.homeYes.length) {
      const currentUpdatedSpreads = {};
      gameObj.homeMoneylines1x2.homeYes.forEach((moneyLineObj, index) => {
        if (moneyLineObj.odds) {
          if (!currentUpdatedSpreads[`_${moneyLineObj.odds}`]) {
            currentUpdatedSpreads[`_${moneyLineObj.odds}`] = [];
          }
          currentUpdatedSpreads[`_${moneyLineObj.odds}`].push(moneyLineObj);
        } else {
          // fillerData
          currentUpdatedSpreads[`fillerData_${index}`] = [moneyLineObj];
        }
      });
      if (Object.keys(currentUpdatedSpreads).length) {
        gameObj.homeMoneylines1x2.homeYes = Object.values(
          currentUpdatedSpreads,
        );
      }
    }

    // Add filler data for homeMoneylines1x2.homeYes
    gameObj.homeMoneylines1x2.homeYes = addFillerData(
      gameObj.homeMoneylines1x2.homeYes,
      'moneyline1x2',
      game?.participants?.[1]?.id,
      null,
      game.id,
      'yes',
    );
  }

  ////  //homeMoneylines1x2.homeNo
  if (gameObj.homeMoneylines1x2 && gameObj.homeMoneylines1x2.homeNo) {
    if (gameObj.homeMoneylines1x2.homeNo.length) {
      const currentUpdatedSpreads = {};
      gameObj.homeMoneylines1x2.homeNo.forEach((moneyLineObj, index) => {
        if (moneyLineObj.odds) {
          if (!currentUpdatedSpreads[`_${moneyLineObj.odds}`]) {
            currentUpdatedSpreads[`_${moneyLineObj.odds}`] = [];
          }
          currentUpdatedSpreads[`_${moneyLineObj.odds}`].push(moneyLineObj);
        } else {
          // fillerData
          currentUpdatedSpreads[`fillerData_${index}`] = [moneyLineObj];
        }
      });
      if (Object.keys(currentUpdatedSpreads).length) {
        gameObj.homeMoneylines1x2.homeNo = Object.values(currentUpdatedSpreads);
      }
    }

    // Add filler data for homeMoneylines1x2.homeNo
    gameObj.homeMoneylines1x2.homeNo = addFillerData(
      gameObj.homeMoneylines1x2.homeNo,
      'moneyline1x2',
      game?.participants?.[1]?.id,
      null,
      game.id,
      'no',
    );
  }

  ////  //draw1x2.drawYes
  if (gameObj.draw1x2 && gameObj.draw1x2.drawYes) {
    if (gameObj.draw1x2.drawYes.length) {
      const currentUpdatedSpreads = {};
      gameObj.draw1x2.drawYes.forEach((moneyLineObj, index) => {
        if (moneyLineObj.odds) {
          if (!currentUpdatedSpreads[`_${moneyLineObj.odds}`]) {
            currentUpdatedSpreads[`_${moneyLineObj.odds}`] = [];
          }
          currentUpdatedSpreads[`_${moneyLineObj.odds}`].push(moneyLineObj);
        } else {
          // fillerData
          currentUpdatedSpreads[`fillerData_${index}`] = [moneyLineObj];
        }
      });
      if (Object.keys(currentUpdatedSpreads).length) {
        gameObj.draw1x2.drawYes = Object.values(currentUpdatedSpreads);
      }
    }

    // Add filler data for draw1x2.drawYes
    gameObj.draw1x2.drawYes = addFillerData(
      gameObj.draw1x2.drawYes,
      'moneyline1x2',
      null,
      null,
      game.id,
      'yes',
    );
  }

  ////  //draw1x2.drawNo
  if (gameObj.draw1x2 && gameObj.draw1x2.drawNo) {
    if (gameObj.draw1x2.drawNo.length) {
      const currentUpdatedSpreads = {};
      gameObj.draw1x2.drawNo.forEach((moneyLineObj, index) => {
        if (moneyLineObj.odds) {
          if (!currentUpdatedSpreads[`_${moneyLineObj.odds}`]) {
            currentUpdatedSpreads[`_${moneyLineObj.odds}`] = [];
          }
          currentUpdatedSpreads[`_${moneyLineObj.odds}`].push(moneyLineObj);
        } else {
          // fillerData
          currentUpdatedSpreads[`fillerData_${index}`] = [moneyLineObj];
        }
      });
      if (Object.keys(currentUpdatedSpreads).length) {
        gameObj.draw1x2.drawNo = Object.values(currentUpdatedSpreads);
      }
    }

    // Add filler data for draw1x2.drawNo
    gameObj.draw1x2.drawNo = addFillerData(
      gameObj.draw1x2.drawNo,
      'moneyline1x2',
      null,
      null,
      game.id,
      'no',
    );
  }

  return gameObj;
}

export function getDeltas(oldSide, newSide) {
  const oldSideHash = {};
  const newSideHash = {};
  let oldSideCounter = 0;
  let newSideCounter = 0;
  let i = 0;
  if (!oldSide) oldSide = [];
  if (!newSide) newSide = [];
  while (
    (oldSideCounter < 4 || newSideCounter < 4) &&
    (i < oldSide.length || i < newSide.length)
  ) {
    // old Side
    if (oldSide[i]) {
      const offerO = oldSide[i];
      if (oldSideHash[offerO.odds]) {
        oldSideHash[offerO.odds] += offerO.sumUntaken;
      } else {
        oldSideHash[offerO.odds] = offerO.sumUntaken;
        oldSideCounter++;
      }
    }
    if (newSide[i]) {
      const offerN = newSide[i];
      if (newSideHash[offerN.odds]) {
        newSideHash[offerN.odds] += offerN.sumUntaken;
      } else {
        newSideHash[offerN.odds] = offerN.sumUntaken;
        newSideCounter++;
      }
    }
    i++;
    if (i > 50) {
      console.log('470 break point ', oldSide.length, newSide.length);
      break;
    }
  }
  let oldSideArray = Object.entries(oldSideHash);
  let newSideArray = Object.entries(newSideHash);
  oldSideArray.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]));
  newSideArray.sort((a, b) => parseFloat(b[0]) - parseFloat(a[0]));
  const oddsDelta = [];
  const volumeDelta = [];
  for (let i = 0; i < 4; i++) {
    if (oldSideArray[i] && newSideArray[i]) {
      if (parseFloat(newSideArray[i][0]) > parseFloat(oldSideArray[i][0])) {
        oddsDelta.push(1);
      } else if (
        parseFloat(newSideArray[i][0]) < parseFloat(oldSideArray[i][0])
      ) {
        oddsDelta.push(-1);
      } else {
        oddsDelta.push(0);
      }
      volumeDelta.push(newSideArray[i][1] - oldSideArray[i][1]);
    } else if (oldSideArray[i] && !newSideArray[i]) {
      oddsDelta.push(-1);
      volumeDelta.push(0 - oldSideArray[i][1]);
    } else if (!oldSideArray[i] && newSideArray[i]) {
      oddsDelta.push(1);
      volumeDelta.push(newSideArray[i][1] - 0);
    } else {
      oddsDelta.push(0);
      volumeDelta.push(0);
    }
  }
  return { oddsDelta, volumeDelta };
}

export function commissionAdjWin(win, commissionPct) {
  return win - win * commissionPct;
}

export function commissionAdjustedRisk(risk, odds, commissionPct) {
  if (odds > 0) return risk * (1 + commissionPct);
  const win = (100 / Math.abs(odds)) * risk;
  const newRisk = risk + win * commissionPct;
  return newRisk;
}

export function displayWithVig(odds, commissionPct, displayDecimal) {
  let commissionAdjustedOdds;
  let risk;
  let win;
  let adjRisk;
  if (odds > 0) {
    risk = 100;
    win = odds;
    adjRisk = risk + risk * commissionPct;
  } else {
    risk = Math.abs(odds);
    win = 100;
    adjRisk = risk + win * commissionPct;
  }
  const adjWin = win - win * commissionPct;
  if (adjWin >= adjRisk) {
    commissionAdjustedOdds = 100 * (adjWin / adjRisk);
  } else {
    commissionAdjustedOdds = -100 * (adjRisk / adjWin);
  }
  return displayDecimal
    ? Math.round(commissionAdjustedOdds * 10) / 10
    : Math.round(commissionAdjustedOdds);
}

export function getSideStringShort(game, session) {
  if (session.type === 'moneyline') {
    return game.participants.find((p) => p.id === session.participantID)
      .shortName;
  } else if (session.type === 'spread') {
    return `${
      game.participants.find((p) => p.id === session.participantID).shortName
    }  ${signOdds(session.spread)}`;
  } else if (session.type === 'total') {
    if (game.league.toLowerCase().includes('1h')) {
      return `1H ${capitalize(session.OU)} ${session.total}`;
    }
    return `${capitalize(session.OU)} ${session.total}`;
  }
  return '';
}

export function getSideStringShorter(game, session) {
  if (session.type === 'moneyline') {
    const team = game.participants.find((p) => p.id === session.participantID);
    if (team && game.league.toLowerCase().includes('future')) {
      return `${team.futuresSide} `;
    }
    return `${team.shortName} `;
  } else if (session.type === 'spread') {
    const participant = game.participants.find(
      (p) => p.id === session.participantID,
    );
    if (participant && participant.shortName) {
      return `${participant.shortName} `;
    }
  } else if (session.type === 'total' && session.OU) {
    return `${capitalize(session.OU).slice(0, 1)} `;
  }
  return '';
}

// Medium/abbreviated team name as shown in sportsbook views (e.g. "COL
// Rockies" rather than "Colorado Rockies"). Mirrors the sportsbook resolution
// in GameOrders: prefer the curated displayName from teamsDisplayNames, then
// the participant's mediumName, then fall back to the long name so nothing
// ever renders blank. Period-prefixed leagues (F5/1H/Q1…) keep their prefix.
export function getTeamDisplayName(participant) {
  if (!participant) return '';
  const longName = participant.longName || '';
  const mapping = lookupTeamDisplay(longName);
  const display =
    (mapping && mapping.displayName) ||
    participant.mediumName ||
    longName ||
    participant.shortName ||
    '';
  return applyPeriodPrefix(longName, display);
}

// The Draw outcome has no participant document, so on period child games
// (1H soccer etc.) it can't inherit the "1H " token from a longName the way
// team labels do — derive it from a participant so draw labels match.
export function getDrawLabel(game) {
  return applyPeriodPrefix(game?.participants?.[0]?.longName, 'Draw');
}

export function getSideString(game, session, showodds = true, useMedium = false) {
  const teamName = (participant) =>
    useMedium ? getTeamDisplayName(participant) : participant.longName;
  let sideString;
  if (session.type === 'moneyline') {
    if (game.league.toLowerCase().includes('future')) {
      const team = game.participants.find(
        (p) => p.id === session.participantID,
      );
      sideString = `${teamName(team)} ${team.futuresSide}`;
    } else {
      sideString = teamName(
        game.participants.find((p) => p.id === session.participantID),
      );
    }
  } else if (session.type === 'spread') {
    const participantSpread = game.participants.find(
      (p) => p.id === session.participantID,
    );
    if (participantSpread) {
      sideString = showodds
        ? `${teamName(participantSpread)}  ${signOdds(session.spread)}`
        : `${teamName(participantSpread)}`;
    }
  } else if (session.type === 'total') {
    if (game.league.toLowerCase().includes('1h')) {
      sideString = showodds
        ? `1H ${capitalize(session.OU)} ${session.total}`
        : `1H ${capitalize(session.OU)} `;
    } else {
      sideString = showodds
        ? `${capitalize(session.OU)} ${session.total}`
        : `${capitalize(session.OU)}`;
    }
  } else if (session.type === 'moneyline1x2') {
    if (session.market === 'draw') {
      sideString = `${capitalize(session.side).toUpperCase()} ${getDrawLabel(game)}`;
    } else {
      const team = game.participants.find((p) => p.id === session.market);
      sideString = `${capitalize(session.side).toUpperCase()} ${teamName(team)}`;
    }
  }
  return sideString;
}

// Quarter lines (.25/.75) only occur on soccer (Asian handicaps / quarter
// totals). They grade as two half-stake legs at the adjacent whole and .5
// lines: the WHOLE-number leg can push (actual lands on it); the .5 leg never
// pushes and is the decisive one — it wins on a half-win, loses on a half-loss.
// Returns a short note string, or '' when not a half result.
export function getHalfLineNote(session) {
  if (session.outcome !== 'half-win' && session.outcome !== 'half-loss')
    return '';
  let line;
  if (session.type === 'spread') line = Number(session.spread);
  else if (session.type === 'total') line = Number(session.total);
  else return '';

  const lower = line - 0.25;
  const upper = line + 0.25;
  // The .5 leg is decisive (won/lost); the whole-number leg pushes.
  const isHalf = (n) => Math.abs(n % 1) === 0.5;
  const liveLeg = isHalf(lower) ? lower : upper;
  const pushLeg = isHalf(lower) ? upper : lower;

  const fmt = (n) =>
    session.type === 'total' ? `${capitalize(session.OU)} ${n}` : signOdds(n);
  const live = session.outcome === 'half-win';
  const head = live ? 'HALF WIN' : 'HALF LOSS';
  const liveWord = live ? 'won' : 'lost';
  return `${head} · ${fmt(liveLeg)} ${liveWord}, ${fmt(pushLeg)} push`;
}

// Structured half-result info for a compact badge: the short label shown on the
// pill ("HALF WIN" / "HALF LOSS") plus the explanatory detail surfaced on
// hover/click (e.g. "+0.5 won, 0 push"). Returns null when not a half result.
export function getHalfLineBadge(session) {
  const note = getHalfLineNote(session);
  if (!note) return null;
  const [label, detail] = note.split(' · ');
  return {
    label,
    detail,
    win: session.outcome === 'half-win',
  };
}

export function getSideStringProps(game, session, showodds = true) {
  let sideString;
  sideString = showodds
    ? `${game.eventName} ${session.OU === 'over' ? 'O' : 'U'}${session.total}`
    : `${game.eventName} ${session.OU === 'over' ? 'O' : 'U'} `;
  return sideString;
}

// Compact label for a bet's market type. Props are detected by league since
// their underlying `type` is usually a total. Soccer spreads are Asian
// Handicaps pre-match, and European Handicaps once the match is live (the line
// is applied on top of the score at the time of the bet). Only the non-obvious
// markets (1X2, Asian/European Handicap) carry a tooltip explaining the bet;
// the rest are self-evident from the label.
export function getMarketTypeMeta(type, league = '', sport = '', live = false) {
  if (league && league.toUpperCase().includes('PROPS')) {
    return { label: 'PROP' };
  }
  switch (type) {
    case 'moneyline':
      return { label: 'MONEYLINE' };
    case 'spread':
      if ((sport || '').toLowerCase() === 'soccer') {
        // Live soccer handicaps are European Handicap, not Asian: the line is
        // applied on top of the score at the time the bet is placed, so an
        // offer phrased as a pre-match Asian Handicap no longer means the same
        // thing once the match is in play.
        if (live) {
          return {
            label: 'EUROPEAN HANDICAP',
            tooltip:
              'European Handicap (live) — a soccer spread applied on top of the score at the time you place the bet. The handicap is added to the result of the remaining match from that point, so your side must beat the line counting from the current scoreline. Settled on 90 minutes plus injury time (no extra time or penalties).',
          };
        }
        return {
          label: 'ASIAN HANDICAP',
          tooltip:
            'Asian Handicap — a soccer spread that removes the draw by giving one side a goal head-start (e.g. -0.5, +1). Your side must beat the handicap; some lines can push and refund. Settled on 90 minutes plus injury time (no extra time or penalties).',
        };
      }
      return { label: 'SPREAD' };
    case 'total':
      return { label: 'TOTAL' };
    case 'moneyline1x2':
      return {
        label: '1X2',
        tooltip:
          'Three-way (1X2) — home win, draw, and away win are three separate outcomes. "Yes" wins if that outcome happens; "No" wins if it does not. Settled on 90 minutes plus injury time (no extra time or penalties).',
      };
    default:
      return { label: (type || '').toString().toUpperCase() };
  }
}

// Analytics: which of the exotic market builds a bet belongs to. The existing
// market_type property collapses everything into moneyline/spread/total/prop
// (plus the raw moneyline1x2 session type), which can't answer "did the June
// World Cup market build (To Advance, 1x2, European handicap, 1H draw lines)
// produce ANY betting volume — keep building exotic markets or stop?". This
// derives one bounded label per bet from the same game/session fields the
// display layer already keys off (isToAdvanceGame, SERIES league qualifier,
// participant period prefix, session type, getMarketTypeMeta's live-soccer
// spread rule), so analytics can never drift from what the user was shown.
//
// Precedence: game-level specials first (to_advance, series, half_line) —
// a 1st-half draw line is a moneyline1x2 on a period child game, and it must
// count as the 1H build, not as a plain 1x2.
export function getMarketSubtype(game, sessionType) {
  if (isToAdvanceGame(game)) return 'to_advance';
  if ((game?.league || '').toString().toUpperCase().includes('SERIES')) {
    return 'series';
  }
  // Period child games ("1H", "F5", "Q1", …) carry the token as a participant
  // longName prefix — the same signal the modals' period pill renders from.
  if (getPeriodPrefix(game?.participants?.[0]?.longName).trim()) {
    return 'half_line';
  }
  if (sessionType === 'moneyline1x2') return '1x2';
  // Mirrors getMarketTypeMeta: a spread on live soccer is a European
  // Handicap. (Pre-match soccer spreads are Asian Handicaps — an older
  // market, deliberately left as 'standard' so euro_handicap isolates the
  // June build.)
  if (
    sessionType === 'spread' &&
    (game?.sport || '').toString().toLowerCase() === 'soccer' &&
    game?.live
  ) {
    return 'euro_handicap';
  }
  return 'standard';
}

// Some leagues carry a qualifier (conference/title/series) that adds useful
// context to a pick — e.g. a moneyline on "NBA East". Surface the matched
// keyword inline next to the name rather than hiding it in a tooltip.
const LEAGUE_QUALIFIERS = ['WINNER', 'SERIES', 'TITLE', 'EAST', 'WEST'];

export function getLeagueQualifier(league = '') {
  const upper = (league || '').toString().toUpperCase();
  return LEAGUE_QUALIFIERS.find((kw) => upper.includes(kw)) || '';
}

// Soccer "outright" markets (e.g. SOCCER-WINNER, TO-ADVANCE World Cup specials)
// come back from the API with isYesNo:false, which would normally trigger the
// 3-way (home/away/draw) rendering. They are really 2-way markets, so we treat
// any league configured in TWO_WAY_SOCCER_LEAGUE_KEYWORDS as 2-way.
export function isSoccerYesNoLeague(league = '') {
  const normalized = (league || '')
    .toString()
    .toUpperCase()
    .replace(/[\s-]/g, '');
  return TWO_WAY_SOCCER_LEAGUE_KEYWORDS.some((kw) => normalized.includes(kw));
}

// "Winner" outright markets whose two sides are "X Wins" / "Does NOT Win"
// (e.g. SOCCER-WINNER World Cup outrights, CUSTOM-GOLF tournament winners).
// These should show the subject's flag/logo on both sides via TeamLogo's
// winner-market mode instead of the usual yes/no logo suppression. Detected by
// league keyword (soccer outrights) or by shape: two participants with
// -YES/-NO shortNames where a side names the subject with "Wins". TeamLogo
// still renders nothing when it can't resolve the subject, so a true here is
// safe for markets whose subject isn't in the team data.
const YES_NO_SHORTNAME_RE = /-(YES|NO)$/i;
const WIN_SUBJECT_RE = /\bwins?\b/i;
export function isWinnerOutrightGame(game) {
  if (isSoccerYesNoLeague(game?.league)) return true;
  const participants = game?.participants;
  if (!Array.isArray(participants) || participants.length !== 2) return false;
  return (
    participants.every((p) => YES_NO_SHORTNAME_RE.test(p?.shortName || '')) &&
    participants.some((p) => WIN_SUBJECT_RE.test(p?.longName || ''))
  );
}

// True only for genuine 1x2 soccer markets (home/draw/away). A soccer game is
// 2-way when the feed marks it isYesNo, its league is configured as a 2-way
// outright market, OR it is a "To Advance" special (which lives in the regular
// WORLD-CUP league but is a 2-way outright) — in which case it renders and
// places through the normal moneyline path (one cross-matching book), not 1x2.
export function isSoccer1x2Market(game) {
  if (game?.sport !== 'soccer') return false;
  return !(
    Boolean(game?.isYesNo) ||
    isSoccerYesNoLeague(game?.league) ||
    isToAdvanceGame(game)
  );
}

// Resolve the side indicator for an individual bet row. Team markets
// (moneyline/spread/1x2) get the relevant team's logo; totals and props get an
// over/under arrow. Accepts one or more bet specs (a rolled-up row may carry
// several wagers) and only returns an icon when they unambiguously share a side.
export function getBetSideIcon(bets, game) {
  const list = (Array.isArray(bets) ? bets : [bets]).filter(Boolean);
  if (!list.length) return { kind: 'none' };

  const league = (game?.league || '').toString().toUpperCase();
  const isProps = league.includes('PROPS');
  const type = list[0].type;
  const participants = Array.isArray(game?.participants)
    ? game.participants
    : [];

  if (isProps || type === 'total') {
    const dirs = new Set(
      list.map((b) =>
        b.OU === 'over' ? 'up' : b.OU === 'under' ? 'down' : null,
      ),
    );
    if (dirs.size === 1 && !dirs.has(null)) {
      return { kind: 'arrow', dir: [...dirs][0] };
    }
    return { kind: 'none' };
  }

  if (type === 'moneyline' || type === 'spread') {
    const ids = new Set(
      list.map((b) => (b.participantID != null ? String(b.participantID) : '')),
    );
    const id = [...ids][0];
    if (ids.size === 1 && id) {
      const participant = participants.find((p) => String(p.id) === id);
      if (participant) return { kind: 'logo', participant };
    }
    return { kind: 'none' };
  }

  if (type === 'moneyline1x2') {
    const markets = new Set(
      list.map((b) => (b.market != null ? String(b.market) : '')),
    );
    const m = [...markets][0];
    if (markets.size === 1 && m) {
      // The draw has no team — mark it with a flag instead of a logo.
      if (m === 'draw') return { kind: 'flag' };
      const participant = participants.find((p) => String(p.id) === m);
      if (participant) return { kind: 'logo', participant };
    }
    return { kind: 'none' };
  }

  return { kind: 'none' };
}

export function calculateAverageOdds(risk, win) {
  if (win >= risk) {
    const averageOdds = 100 * (win / risk);
    return averageOdds;
  }
  return (-100 * risk) / win;
}

export function fixed2(value) {
  if (value < 0) {
    return value < parseInt(value, 10) ? value.toFixed(2) : parseInt(value, 10);
  }
  return value > parseInt(value, 10) ? value.toFixed(2) : parseInt(value, 10);
}

export function containsSpecialChar(field) {
  const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < field.length; i += 1) {
    const char = field.charAt(i).toLowerCase();
    if (!characters.includes(char)) {
      return true;
    }
  }
  return false;
}
export function gamesSearch(games, value) {
  return games.filter((game) => {
    const gameTitle = `#${game.participants[0].rotationNumber} ${game.participants[0].longName} ${game.participants[0].shortName} @ #${game.participants[1].rotationNumber} ${game.participants[1].longName} ${game.participants[1].shortName}`;
    return gameTitle.toLowerCase().includes(value.toLowerCase());
  });
}

export function getMainAwaySpread(game) {
  if (game.mainAwaySpread || game.mainAwaySpread === 0) {
    return game.mainAwaySpread;
  }
  return Object.keys(game.awaySpreads).sort((x, y) => x - y)[1];
}

export function getMainHomeSpread(game) {
  if (game.mainHomeSpread || game.mainHomeSpread === 0) {
    return game.mainHomeSpread;
  }
  return Object.keys(game.homeSpreads).sort((x, y) => x - y)[1];
}

export function hasFourcasterMarketOddsSession(sessions, userId) {
  if (sessions && sessions.length && userId) {
    const fourcasterSession = sessions.find(
      (session) =>
        session.createdBy &&
        session.createdBy.toString() !== userId.toString() &&
        session.odds > session.marketOdds,
    );

    if (fourcasterSession) {
      return true;
    }
  }
  return false;
}

export function hasFourcasterSession(sessions, userId) {
  if (sessions && sessions.length && userId) {
    const fourcasterSession = sessions.find(
      (session) =>
        session.source !== 'fillerData' &&
        session.createdBy &&
        session.createdBy.toString() !== userId.toString() &&
        session.odds >= session.pinnacleOdds,
    );

    if (fourcasterSession) {
      return true;
    }
  }
  return false;
}

export function calculateSumUntaken(sessions) {
  if (!sessions || !sessions[0] || !sessions[0].odds) {
    return 0;
  }
  return sessions.reduce(
    (totalValue, currentValue) => totalValue + currentValue.sumUntaken,
    0,
  );
}

export function getCommissionPct(userPct, league, betType) {
  return commissionDiscounts[league]?.[betType]
    ? Math.min(commissionDiscounts[league][betType], userPct)
    : userPct;
}

export function getOtherSideString(game, session, showodds = true) {
  let sideString;
  if (session.type === 'moneyline') {
    if (game.league.toLowerCase().includes('future')) {
      const team = game.participants.find(
        (p) => p.id !== session.participantID,
      );
      sideString = `${team.longName} ${team.futuresSide}`;
    } else {
      sideString = game.participants.find(
        (p) => p.id !== session.participantID,
      ).longName;
    }
  } else if (session.type === 'spread') {
    const participantSpread = game.participants.find(
      (p) => p.id !== session.participantID,
    );
    if (participantSpread) {
      sideString = showodds
        ? `${participantSpread.longName}  ${signOdds(session.spread)}`
        : `${participantSpread.longName}`;
    }
  } else if (session.type === 'total') {
    const othersideOU = session.OU === 'over' ? 'under' : 'over';
    if (game.league.toLowerCase().includes('1h')) {
      sideString = showodds
        ? `1H ${capitalize(othersideOU)} ${session.total}`
        : `1H ${capitalize(othersideOU)} `;
    } else {
      sideString = showodds
        ? `${capitalize(othersideOU)} ${session.total}`
        : `${capitalize(othersideOU)}`;
    }
  } else if (session.type === 'moneyline1x2') {
    if (session.market !== 'draw') {
      const team = game.participants.find((p) => p.id === session.market);
      sideString = `${capitalize(session.side)} ${team.longName}`;
      if (game.league.toLowerCase().includes('future')) {
        sideString = session.side === 'yes' ? 'NO' : 'YES';
        const team = game.participants.find((p) => p.id === session.market);
        sideString = `${capitalize(sideString)} ${team.longName}`;
      } else {
        sideString = session.side === 'yes' ? 'NO' : 'YES';
        const team = game.participants.find((p) => p.id === session.market);
        sideString = `${capitalize(sideString)} ${team.longName}`;
      }
    } else {
      sideString = `${session.side === 'yes' ? 'NO' : 'YES'} ${getDrawLabel(game)}`;
    }
  }
  return sideString;
}

export function getOrderSideString(game, session) {
  const futuresSides = ['Yes', 'No'];
  if (session.type === 'moneyline') {
    const team = game.participants.find((p) => p.id !== session.participantID);
    if (game.league.toLowerCase().includes('future')) {
      return `${team.longName} ${futuresSides.find((o) => o === team.futuresSide)}`;
    }
    return `${team.longName}`;
  } else if (session.type === 'spread') {
    return `${
      game.participants.find((p) => p.id !== session.participantID).longName
    }  ${signOdds(-session.spread)}`;
  } else if (session.type === 'total') {
    if (game.league.toLowerCase().includes('1h')) {
      return `1H ${capitalize(['over', 'under'].find((ou) => ou !== session.OU))} ${session.total}`;
    }
    return `${capitalize(['over', 'under'].find((ou) => ou !== session.OU))} ${
      session.total
    }`;
  } else if (session.type === 'moneyline1x2') {
    const side = session.side.toUpperCase() === 'YES' ? 'NO' : 'YES';
    if (session.market !== 'draw') {
      if (game.league.toLowerCase().includes('future')) {
        const team = game.participants.find((p) => p.id === session.market);
        return `${team.longName} ${team.futuresSide}`;
      } else {
        return `${side} ${
          game.participants.find((p) => p.id === session.market).longName
        }`;
      }
    } else {
      return `${side} ${getDrawLabel(game)}`;
    }
  }
  return '';
}

export function getBestOdds(selectedGame, session) {
  if (!session.default) {
    let bestOdds;
    const homeSpread = selectedGame.homeSpreads[session.spread]
      ? selectedGame.homeSpreads[session.spread]
      : selectedGame.homeSpreads[-session.spread];
    if (session.type === 'total') {
      if (session.OU === 'over') {
        bestOdds =
          selectedGame.over[session.total] &&
          selectedGame.over[session.total].length > 0
            ? selectedGame.over[session.total][0].odds
            : 0;
      } else {
        bestOdds =
          selectedGame.under[session.total] &&
          selectedGame.under[session.total].length > 0
            ? selectedGame.under[session.total][0].odds
            : 0;
      }
    } else if (session.type === 'moneyline') {
      if (selectedGame.homeMoneylines.find((s) => s.id === session.id)) {
        bestOdds = selectedGame.homeMoneylines[0].odds;
      } else {
        bestOdds = selectedGame.awayMoneylines[0].odds;
      }
    } else if (homeSpread.find((s) => s.id === session.id)) {
      bestOdds =
        selectedGame.homeSpreads[session.spread] &&
        selectedGame.homeSpreads[session.spread].length > 0
          ? selectedGame.homeSpreads[session.spread][0].odds
          : 0;
    } else {
      bestOdds =
        selectedGame.awaySpreads[session.spread] &&
        selectedGame.awaySpreads[session.spread].length > 0
          ? selectedGame.awaySpreads[session.spread][0].odds
          : 0;
    }
    return bestOdds;
  }
  return 0;
}

export function findBestOffer(sessions) {
  const finalValue =
    (Array.isArray(sessions) ? sessions : [])
      .map((session) => session.odds)
      .sort((x, y) => y - x)[0] || 0;
  return finalValue;
}

export function getOtherSideType(sideType) {
  if (sideType === 'homeMoneylines') return 'awayMoneylines';
  if (sideType === 'awayMoneylines') return 'homeMoneylines';
  if (sideType === 'homeSpreads') return 'awaySpreads';
  if (sideType === 'awaySpreads') return 'homeSpreads';
  if (sideType === 'over') return 'under';
  if (sideType === 'under') return 'over';
  if (sideType.homeAwayDraw === 'awayMoneylines1x2') {
    if (sideType.side === 'yes') {
      return { homeAwayDraw: sideType.homeAwayDraw, side: 'No' };
    } else {
      return { homeAwayDraw: sideType.homeAwayDraw, side: 'Yes' };
    }
  } else if (sideType.homeAwayDraw === 'homeMoneylines1x2') {
    if (sideType.side === 'yes') {
      return { homeAwayDraw: sideType.homeAwayDraw, side: 'No' };
    } else {
      return { homeAwayDraw: sideType.homeAwayDraw, side: 'Yes' };
    }
  } else if (sideType.homeAwayDraw === 'draw1x2') {
    if (sideType.side === 'yes') {
      return { homeAwayDraw: sideType.homeAwayDraw, side: 'No' };
    } else {
      return { homeAwayDraw: sideType.homeAwayDraw, side: 'Yes' };
    }
  }
}

export function convertDecimalToAmerican(decimalOdds) {
  if (parseFloat(decimalOdds) === 2) {
    return -100;
  } else if (parseFloat(decimalOdds) > 2.0) {
    return (decimalOdds - 1) * 100;
  }
  return -100 / (decimalOdds - 1);
}

export function roundInt(value) {
  const n = Number.parseFloat(value);
  return n.toFixed(0);
}

export function round1(number) {
  return Math.round(number * 10) / 10;
}


export function getOrder(selectedGame, selectedOrder) {
  const session = selectedOrder;
  let [homeSpread, awaySpread] = [];
  if (session.type !== 'total') {
    [homeSpread, awaySpread] = getMainSpreads(selectedGame);
  }

  if (
    session.type === 'spread' &&
    Math.abs(session.spread) !== Math.abs(homeSpread)
  ) {
    const { homeAway } = selectedGame.participants.find(
      (p) => p.id === session.participantID,
    );
    if (homeAway === 'home') {
      homeSpread = session.spread;
      awaySpread = -session.spread;
    } else if (homeAway === 'away') {
      awaySpread = session.spread;
      homeSpread = -session.spread;
    }
  }
  let mainTotal = getMainTotal(selectedGame);
  if (session.type === 'total' && session.total !== mainTotal) {
    mainTotal = session.total;
  }
  const selectedIsFutures = selectedGame.league
    .toLowerCase()
    .includes('future');
  let initializedAwaySpread;
  let initializedHomeSpread;
  let initializedOver = [];
  let initializedUnder = [];

  let initializedHomeML = [];
  let initializedAwayML = [];
  if (selectedGame.homeMoneylines && selectedGame.homeMoneylines.length > 0) {
    initializedHomeML =
      selectedGame.homeMoneylines.length > 0 &&
      selectedGame.homeMoneylines[0].source !== 'fillerData'
        ? selectedGame.homeMoneylines
        : [];
    initializedAwayML =
      selectedGame.awayMoneylines.length > 0 &&
      selectedGame.awayMoneylines[0].source !== 'fillerData'
        ? selectedGame.awayMoneylines
        : [];
  }

  /* let initializedHomeML = [];
    let initializedAwayML = [];
      if(selectedGame.homeMoneylines && selectedGame.homeMoneylines.length > 0){
        initializedHomeML =
      } */

  // const initializedHomeML =
  //   selectedGame.homeMoneylines &&
  //   selectedGame.homeMoneylines[0] &&
  //   selectedGame.homeMoneylines[0].source !== 'fillerData'
  //     ? selectedGame.homeMoneylines[0]
  //     : [];

  // const initializedAwayML =
  //   selectedGame.homeMoneylines &&
  //   selectedGame.homeMoneylines[0] &&
  //   selectedGame.awayMoneylines[0].source !== 'fillerData'
  //     ? selectedGame.awayMoneylines[0]
  //     : [];

  if (session.type === 'spread' && !selectedIsFutures) {
    if (
      selectedGame.awaySpreads[-homeSpread] &&
      selectedGame.awaySpreads[awaySpread]
    ) {
      initializedAwaySpread = selectedGame.awaySpreads[-homeSpread]
        ? selectedGame.awaySpreads[awaySpread]
        : [];
    } else {
      initializedAwaySpread = [];
    }
    // initializedAwaySpread =
    //   selectedGame.awaySpreads[-homeSpread] &&
    //   selectedGame.awaySpreads[-homeSpread][0]
    //     ? selectedGame.awaySpreads[awaySpread][0]
    //     : [];
    // initializedHomeSpread =
    //   selectedGame.homeSpreads[-awaySpread] &&
    //   selectedGame.homeSpreads[-awaySpread][0]
    //     ? selectedGame.homeSpreads[homeSpread][0]
    //     : [];
    if (
      selectedGame.homeSpreads[-awaySpread] &&
      selectedGame.homeSpreads[homeSpread]
    ) {
      initializedHomeSpread = selectedGame.homeSpreads[-awaySpread]
        ? selectedGame.homeSpreads[homeSpread]
        : [];
    } else {
      initializedHomeSpread = [];
    }

    // initializedOver =
    //   selectedGame.over[mainTotal] &&
    //   selectedGame.over[mainTotal][0]
    //     ? selectedGame.over[mainTotal][0]
    //     : [];
    initializedOver = selectedGame.over[mainTotal]
      ? selectedGame.over[mainTotal]
      : [];
    initializedUnder = selectedGame.under[mainTotal]
      ? selectedGame.under[mainTotal]
      : [];
    // initializedUnder =
    //   selectedGame.under[mainTotal] &&
    //   selectedGame.under[mainTotal][0]
    //     ? selectedGame.under[mainTotal][0]
    //     : [];
  }
  const game = {
    id: selectedGame.id,
    isFutures: selectedGame.isFutures,
    eventName: selectedGame.eventName,
    league: selectedGame.league,
    sport: selectedGame.sport,
    start: selectedGame.start,
    ended: selectedGame.ended,
    participants: selectedGame.participants,
    moneyline: [...initializedAwayML, ...initializedHomeML],
    spread:
      session.type === 'spread' && !selectedIsFutures
        ? [...initializedAwaySpread, ...initializedHomeSpread]
        : [],
    total: !selectedIsFutures ? [...initializedOver, ...initializedUnder] : [],
  };
  /* if (
      session.type === 'moneyline' &&
      selectedGame.awayMoneylines.length > 1
    ) {
      if (selectedGame.awayMoneylines[1].id === session.id) {
        // eslint-disable-next-line prefer-destructuring
        game.moneyline[0] = selectedGame.awayMoneylines[1];
      }
      if (selectedGame.homeMoneylines[1].id === session.id) {
        // eslint-disable-next-line prefer-destructuring
        game.moneyline[1] = selectedGame.homeMoneylines[1];
      }
    } else if (
      session.type === 'spread' &&
      selectedGame.awaySpreads.length > 1
    ) {
      if (selectedGame.awaySpreads[awaySpread][1].id === session.id) {
        // eslint-disable-next-line prefer-destructuring
        game.spread[0] = selectedGame.awaySpreads[awaySpread][1];
      }
      if (selectedGame.homeSpreads[homeSpread][1].id === session.id) {
        // eslint-disable-next-line prefer-destructuring
        game.spread[1] = selectedGame.homeSpreads[homeSpread][1];
      }
    } else if (
      session.type === 'total' &&
      selectedGame.over.length > 1
    ) {
      if (selectedGame.over[mainTotal][1].id === session.id) {
        // eslint-disable-next-line prefer-destructuring
        game.total[0] = selectedGame.over[mainTotal][1];
      }
      if (selectedGame.under[mainTotal][1].id === session.id) {
        // eslint-disable-next-line prefer-destructuring
        game.total[1] = selectedGame.under[mainTotal][1];
      }
    } */
  return { game, session };
}

export function evaluateSide(orderObject, game) {
  const { type, OU, participantID, side, market } = orderObject;
  if (type === 'total') {
    return {
      side: OU,
      otherSide: OU === 'under' ? 'over' : 'under',
    };
  } else if (type === 'moneyline1x2') {
    let sideM, otherSide;
    if (market !== 'draw') {
      if (game.participants[0].id === market) {
        sideM = `away${capitalize(side)}`;
        otherSide = `home${capitalize(side)}`;
      } else if (game.participants[1].id === market) {
        sideM = `home${capitalize(side)}`;
        otherSide = `away${capitalize(side)}`;
      }
    } else {
      sideM = `draw${capitalize(side)}`;
    }
    return { side: sideM, otherSide };
  } else {
    let side, otherSide;
    if (game.participants[0].id === participantID) {
      side = `away${capitalize(type)}s`;
      otherSide = `home${capitalize(type)}s`;
    } else if (game.participants[1].id === participantID) {
      side = `home${capitalize(type)}s`;
      otherSide = `away${capitalize(type)}s`;
    }
    return { side, otherSide };
  }
}
export function convertPercentToAmerican(x) {
  if (x > 0.5) {
    const y = 1 - x;
    const z = -1 * (x / y) * 100;
    return z;
  }
  if (x < 0.5) {
    const y = 1 - x;
    const z = (y / x) * 100;
    return z;
  }
  const z = 100;
  return z;
}

export function convertAmericanToPercent(odds) {
  const z = Number(odds);
  if (z > 0) {
    const y = 100 / (z + 100);
    return y;
  }
  if (z < 0) {
    const y = z / (z - 100);
    return y;
  }
  return null;
}

export function convertPercentToAmericanWithVig(percent, vig) {
  const x = percent;
  const y = 1 - x;
  const pie = 1 + vig;
  const homePercent = (x / 1) * pie;
  const homeOdds = Math.round(convertPercentToAmerican(homePercent));
  const awayPercent = (y / 1) * pie;
  const awayOdds = Math.round(convertPercentToAmerican(awayPercent));
  return [homeOdds, awayOdds];
}

export function findOffset(ourAbsDiff, maxDiff) {
  let ourOffset;
  if (ourAbsDiff < 0.12 && ourAbsDiff >= 0.003) {
    ourOffset =
      (-(49.417 * ourAbsDiff ** 2) + 13.383 * ourAbsDiff + 0.0729) * maxDiff;
  } else if (ourAbsDiff < 0.003) {
    ourOffset = 0;
  } else if (ourAbsDiff >= 0.12) {
    ourOffset = maxDiff;
  }
  return ourOffset;
}

export function offensiveNoVigPercent(ourOffset, ourLineDiff, avgHwp) {
  let noVigP;
  if (ourLineDiff > 0) {
    noVigP = avgHwp + ourOffset;
  } else {
    noVigP = avgHwp - ourOffset;
  }
  return noVigP;
}

export function addLean(americanOdds, lean) {
  let leanCopy = lean;
  if (!leanCopy) {
    leanCopy = -1;
  }
  const leanedOdds = parseFloat(americanOdds) + parseFloat(leanCopy);
  if (Math.abs(leanedOdds) >= 100) {
    return leanedOdds;
  } else if (Math.sign(americanOdds) > 0 && Math.abs(leanedOdds) < 100) {
    // odds are positive
    return leanedOdds - 200;
  }
  return 200 + leanedOdds;
}

export function calculateNoVig(line1, line2) {
  const totalPie = line1 + line2;
  const noVig = line1 / totalPie;
  return noVig;
}

export function calculateHomeWinPct(americanLine1, americanLine2) {
  const line1 = convertAmericanToPercent(americanLine1);
  const line2 = convertAmericanToPercent(americanLine2);
  const noVig = calculateNoVig(line1, line2);
  return noVig;
}

export function subtractOdds(newOdds, oldOdds) {
  if (Math.sign(newOdds) !== Math.sign(oldOdds)) {
    if (Math.sign(newOdds) < 1) {
      return newOdds - oldOdds + 200;
    }
    return newOdds - oldOdds - 200;
  }
  return newOdds - oldOdds;
}

export function compareGameOdds(oldGame, newGame) {
  let availableBetTypes = Object.keys(newGame).filter(
    (o) => !betTypeKeys.includes(o),
  );
  if (oldGame.league.toLowerCase().includes('future')) {
    availableBetTypes = ['homeMoneylines', 'awayMoneylines'];
  }
  const returnObject = {};
  availableBetTypes.map((betType) => {
    const oldLines = oldGame[betType];
    const newLines = newGame[betType];
    if (!oldLines || !newLines) {
      return;
    }
    const isMoneyline = betType.toLowerCase().includes('money');
    let oldOdds;
    let newOdds;
    let number;
    if (isMoneyline) {
      oldOdds = oldLines.map((o) => o.odds);
      newOdds = newLines.map((o) => o.odds);
    } else {
      number = Object.keys(newLines).sort(
        (x, y) => Math.abs(x) - Math.abs(y),
      )[1];
      const oldSide = oldLines[number];
      const newSide = newLines[number];
      if (!Array.isArray(oldGames) || !Array.isArray(newGames)) {
        return;
      }
      oldOdds = oldSide.map((o) => o.odds);
      newOdds = newSide.map((o) => o.odds);
    }
    const oddsDifferences = newOdds.map((odds, levelIx) => {
      const oldLevelOdds = oldOdds[levelIx];
      return subtractOdds(odds, oldLevelOdds);
    });
    if (isMoneyline) {
      returnObject[betType] = oddsDifferences;
    } else {
      returnObject[betType] = { [number]: oddsDifferences };
    }
  });

  return returnObject;
}

export function currentDevice() {
  const typeNavigator = window.navigator.userAgent;
  let device = 'Pc';
  if (
    typeNavigator.includes('Mobile') ||
    typeNavigator.includes('Android') ||
    typeNavigator.includes('iPhone') ||
    typeNavigator.includes('iPad')
  ) {
    device = 'Mobile';
  } else {
    device = 'Pc';
  }
  return device;
}

export function addReferreds(signUpCodes, players) {
  const listPlayers = [players];
  const listCodes = [signUpCodes];
  listCodes.map((c, indexc) => {
    listPlayers.map((p, indexp) => {
      p.map((pp, indexpp) => {
        const numRef = Object.prototype.hasOwnProperty.call(pp, 'infoReferred')
          ? pp.infoReferred.numRef
          : null;
        if (numRef > 0) {
          c[pp.p2pCode] = pp.username;
        }
      });
    });
  });
  return listCodes[0];
}

export function sortPlayers(players, column, isDescending) {
  const sortOrder = isDescending ? 1 : -1;
  const sortFunctions = {
    balance: function (a, b) {
      return (a.displayBalance - b.displayBalance) * sortOrder;
    },
    liability: function (a, b) {
      return (a.liability - b.liability) * sortOrder;
    },
    credit: function (a, b) {
      return (a.creditLimit - b.creditLimit) * sortOrder;
    },
    volume: function (a, b) {
      return (a.volume - b.volume) * sortOrder;
    },
    invitedFriends: function (a, b) {
      return (a.infoReferred.numRef - b.infoReferred.numRef) * sortOrder;
    },
    username: function (a, b) {
      return a.username.localeCompare(b.username) * sortOrder;
    },
  };

  return [...players].sort(
    sortFunctions[column] ||
      function (a, b) {
        return b.displayBalance - a.displayBalance;
      },
  );
}

export function validateEmailFormat(email) {
  return /^(\w+([-+.]\w+)*)?@\w+([-.]\w+)*\.\w+([-.]\w+)*$/.test(email);
}

// side is ppt Id if ml or spread, over or under if total
export function getBestSession(game, betType, side, number) {
  const isHome =
    game.participants.find((ppt) => side === ppt.id)?.homeAway === 'home';
  if (betType === 'moneyline') {
    return isHome
      ? game.homeMoneylines[0] || null
      : game.awayMoneylines[0] || null;
  } else if (betType === 'spread') {
    return isHome
      ? game.homeSpreads[number]?.[0] || null
      : game.awaySpreads[number]?.[0] || null;
  } else {
    return side === 'over'
      ? game.over[number]?.[0] || null
      : game.under[number]?.[0] || null;
  }
}

export function createNewGameFromOldGame(oldGame, orderObject) {
  orderObject.mainHomeSpread = orderObject.mainHomeSpread ?? 0;
  orderObject.mainAwaySpread = orderObject.mainAwaySpread ?? 0;
  const {
    sideOrders,
    mainHomeSpread,
    mainAwaySpread,
    mainTotal,
    type,
    spread,
    total,
    market,
  } = orderObject;
  const newGame = { ...oldGame };
  // const [bestOrder] = sideOrders;
  const { side, otherSide } = evaluateSide(orderObject, newGame);
  if (type === 'moneyline') {
    const { oddsDelta, volumeDelta } = getDeltas(oldGame[side], sideOrders);
    newGame[side] = sideOrders;
    if (!newGame.oddsDelta) newGame.oddsDelta = {};
    newGame.oddsDelta[side] = oddsDelta;
    if (!newGame.volumeDelta) newGame.volumeDelta = {};
    newGame.volumeDelta[side] = volumeDelta;
  } else if (type === 'total') {
    const { oddsDelta, volumeDelta } = getDeltas(
      newGame[side][total],
      sideOrders,
    );
    // if this is the last offer being removed reset total back to 0
    if (mainTotal === undefined && sideOrders.length === 0) {
      newGame[otherSide] = {};
      newGame[side] = {};
      newGame.mainTotal = 0;
      newGame[otherSide][0] = [];
      newGame[side][0] = [];
    } else if (
      sideOrders.length === 0 &&
      (newGame[otherSide][total] === undefined ||
        newGame[otherSide][total].length === 0)
    ) {
      // check if the only order is removed from a total val so we shouldn't show it as an alternate any more and set the main total to the default one
      delete newGame[side][total];
      delete newGame[otherSide][total];
      newGame.mainTotal = orderObject.mainTotal;
      newGame.over[orderObject.mainTotal] = newGame.over[orderObject.mainTotal]
        ? newGame.over[orderObject.mainTotal]
        : [];
      newGame.under[orderObject.mainTotal] = newGame.under[
        orderObject.mainTotal
      ]
        ? newGame.under[orderObject.mainTotal]
        : [];
    } else if (
      sideOrders.length === 0 &&
      newGame[otherSide][total]?.length === 0
    ) {
      // if this is remove the last order of a total, and no other side orders on the total, remove the total and set the main total to the default one
      delete newGame[side][total];
      delete newGame[otherSide][total];
      newGame.mainTotal = orderObject.mainTotal;
    } else {
      if (!newGame.mainTotal) {
        delete newGame[side][0];
        delete newGame[otherSide][0];
        newGame.mainTotal = mainTotal;
      }
      newGame[side][total] = sideOrders;
      if (!newGame[otherSide][total]) {
        newGame[otherSide][total] = [];
      }
      if (newGame[side][total].length && newGame[otherSide][total].length) {
        newGame.mainTotal = mainTotal;
      }
      if (!newGame.oddsDelta) newGame.oddsDelta = {};
      newGame.oddsDelta[side] = {
        [total]: oddsDelta,
      };
      if (!newGame.volumeDelta) newGame.volumeDelta = {};
      newGame.volumeDelta[side] = {
        [total]: volumeDelta,
      };
    }
  } else if (type === 'spread') {
    const { oddsDelta, volumeDelta } = getDeltas(
      newGame[side][spread],
      sideOrders,
    );
    // if this is the last offer being removed, also remove the spreads and main spreads
    if (
      !orderObject.mainAwaySpread &&
      !orderObject.mainHomeSpread &&
      newGame.mainAwaySpread &&
      newGame.mainHomeSpread &&
      sideOrders.length === 0
    ) {
      // if this is a live game
      newGame[otherSide] = {};
      newGame[side] = {};
      newGame[otherSide][0] = [];
      newGame[side][0] = [];
      delete newGame.mainAwaySpread;
      delete newGame.mainHomeSpread;
    } else if (
      sideOrders.length === 0 &&
      newGame[otherSide][-spread]?.length === 0
    ) {
      // if this is remove the last order of a spread, and no other side orders on the spread, remove the spread and set the main spreads to the default ones
      delete newGame[side][spread];
      delete newGame[otherSide][-spread];
      newGame.mainAwaySpread = orderObject.mainAwaySpread;
      newGame.mainHomeSpread = orderObject.mainHomeSpread;
      newGame.awaySpreads[newGame.mainAwaySpread] = newGame.awaySpreads[
        orderObject.mainAwaySpread
      ]
        ? newGame.awaySpreads[orderObject.mainAwaySpread]
        : [];
      newGame.homeSpreads[newGame.mainHomeSpread] = newGame.homeSpreads[
        orderObject.mainHomeSpread
      ]
        ? newGame.homeSpreads[orderObject.mainHomeSpread]
        : [];
    } else {
      if (!newGame.mainAwaySpread && !newGame.mainHomeSpread) {
        newGame.mainAwaySpread = mainAwaySpread;
        newGame.mainHomeSpread = mainHomeSpread;
      }
      newGame[side][spread] = sideOrders;
      // make sure that if the other side does not have the value it is set to empty array
      const reverseSpread = -1 * Number(spread);
      if (!newGame[otherSide][reverseSpread]) {
        newGame[otherSide][reverseSpread] = [];
      }
      if (
        newGame[side][spread].length &&
        newGame[otherSide][reverseSpread].length
      ) {
        newGame.mainAwaySpread = mainAwaySpread;
        newGame.mainHomeSpread = mainHomeSpread;
      }
      if (!newGame.oddsDelta) newGame.oddsDelta = {};
      newGame.oddsDelta[side] = {
        [spread]: oddsDelta,
      };
      if (!newGame.volumeDelta) newGame.volumeDelta = {};
      newGame.volumeDelta[side] = {
        [spread]: volumeDelta,
      };
    }
  } else if (type === 'moneyline1x2') {
    // 1x2 sides live nested under awayMoneylines1x2 / homeMoneylines1x2 / draw1x2,
    // not at the top level. Read the old side from the same nested location the
    // new data is written to; otherwise oldGame[side] is always undefined and
    // getDeltas treats every offer as new, flashing the cell on every update.
    const oldContainer =
      market === 'draw'
        ? oldGame.draw1x2
        : side.includes('away')
          ? oldGame.awayMoneylines1x2
          : oldGame.homeMoneylines1x2;
    const oldSide = oldContainer ? oldContainer[side] : undefined;
    const { oddsDelta, volumeDelta } = getDeltas(oldSide, sideOrders);
    if (market !== 'draw') {
      if (side.includes('away')) {
        newGame.awayMoneylines1x2[side] = sideOrders;
      } else {
        newGame.homeMoneylines1x2[side] = sideOrders;
      }
      if (!newGame.oddsDelta) newGame.oddsDelta = {};
      if (!newGame.oddsDelta.awayMoneylines1x2) newGame.oddsDelta.awayMoneylines1x2 = {};
      if (!newGame.oddsDelta.homeMoneylines1x2) newGame.oddsDelta.homeMoneylines1x2 = {};
      if (side.includes('away')) {
        newGame.oddsDelta.awayMoneylines1x2[side] = oddsDelta;
      } else {
        newGame.oddsDelta.homeMoneylines1x2[side] = oddsDelta;
      }
      if (!newGame.volumeDelta) newGame.volumeDelta = {};
      if (!newGame.volumeDelta.awayMoneylines1x2) newGame.volumeDelta.awayMoneylines1x2 = {};
      if (!newGame.volumeDelta.homeMoneylines1x2) newGame.volumeDelta.homeMoneylines1x2 = {};
      if (side.includes('away')) {
        newGame.volumeDelta.awayMoneylines1x2[side] = volumeDelta;
      } else {
        newGame.volumeDelta.homeMoneylines1x2[side] = volumeDelta;
      }
    } else {
      newGame.draw1x2[side] = sideOrders;
      if (!newGame.oddsDelta) newGame.oddsDelta = {};
      if (!newGame.oddsDelta.draw1x2) newGame.oddsDelta.draw1x2 = {};
      newGame.oddsDelta.draw1x2[side] = oddsDelta;
      if (!newGame.volumeDelta) newGame.volumeDelta = {};
      if (!newGame.volumeDelta.draw1x2) newGame.volumeDelta.draw1x2 = {};
      newGame.volumeDelta.draw1x2[side] = volumeDelta;
    }
  }
  return newGame;
}

export function stripOrderbookData(bet) {
  const {
    homeMoneylines,
    awayMoneylines,
    homeSpreads,
    awaySpreads,
    over,
    under,
    homeMoneylines1x2,
    awayMoneylines1x2,
    draw1x2,
    ...rest
  } = bet;
  return rest;
}
